/**
 * Utilidades para cálculos de geolocalización
 */

const RADIO_TIERRA_KM = 6371.0;

/**
 * Representa un punto geográfico
 */
export interface PuntoGeografico {
    lat: number;
    lng: number;
}

/**
 * Representa un marcador en el mapa con información del centro
 */
export interface GoogleMapMarker {
    position: PuntoGeografico;
    nombre: string;
    direccion?: string;
    id?: number;
    [key: string]: any;
}

/**
 * Clase de utilidades para geolocalización
 */
export class UtilsGeoLoc {
    /**
     * Convierte grados a radianes
     */
    private gradosARadianes(grados: number): number {
        return grados * (Math.PI / 180);
    }

    /**
     * Calcula la distancia entre dos puntos geográficos usando la fórmula de Haversine.
     * @param p1 Primer punto geográfico
     * @param p2 Segundo punto geográfico
     * @returns Distancia en metros
     */
    public calcularDistanciaHaversine(p1: PuntoGeografico, p2: PuntoGeografico): number {
        const dLat = this.gradosARadianes(p2.lat - p1.lat);
        const dLng = this.gradosARadianes(p2.lng - p1.lng);

        const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(this.gradosARadianes(p1.lat)) * Math.cos(this.gradosARadianes(p2.lat)) *
            Math.sin(dLng / 2) * Math.sin(dLng / 2);

        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return RADIO_TIERRA_KM * c * 1000; // metros
    }

    /**
     * Encuentra el marcador más cercano a una ubicación dada, usando distancia en línea recta.
     * @param centros Lista de centros disponibles
     * @param ubicacion Ubicación de referencia
     * @returns El centro más cercano o undefined si no hay centros
     */
    public obtenerCentroMasCercanoLineal(
        centros: GoogleMapMarker[],
        ubicacion: PuntoGeografico
    ): GoogleMapMarker | undefined {
        if (!centros || centros.length === 0) {
            return undefined;
        }

        const ordenados = centros
            .map(centro => ({
                centro,
                distancia: this.calcularDistanciaHaversine(ubicacion, centro.position)
            }))
            .sort((a, b) => a.distancia - b.distancia);

        return ordenados.length > 0 ? ordenados[0].centro : undefined;
    }

    /**
     * Ordena una lista de marcadores desde el más cercano al más lejano respecto a una ubicación.
     * @param centros Lista de centros a ordenar
     * @param ubicacion Ubicación de referencia
     * @returns Lista ordenada por proximidad con distancias calculadas
     */
    public ordenarPorProximidad(
        centros: GoogleMapMarker[],
        ubicacion: PuntoGeografico
    ): Array<GoogleMapMarker & { distancia: number }> {
        if (!centros || centros.length === 0) {
            return [];
        }

        return centros
            .map(centro => ({
                ...centro,
                distancia: this.calcularDistanciaHaversine(ubicacion, centro.position)
            }))
            .sort((a, b) => a.distancia - b.distancia);
    }

    /**
     * Genera una cadena de puntos para usar en la URL de Google Directions o Distance Matrix.
     * @param puntos Lista de puntos geográficos
     * @returns Cadena formateada para URL de Google
     */
    public formatearPuntosParaUrl(puntos: PuntoGeografico[]): string {
        return puntos
            .map(p => `${p.lat},${p.lng}`)
            .join('|');
    }

    /**
     * Formatea la distancia en metros a un formato legible
     * @param metros Distancia en metros
     * @returns Cadena formateada (ej: "1.5 km" o "500 m")
     */
    public formatearDistancia(metros: number): string {
        if (metros >= 1000) {
            return `${(metros / 1000).toFixed(2)} km`;
        }
        return `${metros.toFixed(0)} m`;
    }
}

// Exportar una instancia singleton
export const utilsGeoLoc = new UtilsGeoLoc();
